import { useMutation } from "@apollo/client";
import {
  useCreateEncryptionContext,
  useCreateFileMutation,
  useDeleteFileMutation,
  usePostReceiverDocsApiFile,
} from "apollo/hooks/mutations";
import {
  useGetSelectableCareprovidersLazy,
  useLazyGetReceiverDocsApiPublicKeys,
} from "apollo/hooks/queries";
import { useEnvContext } from "context/EnvContext";
import { UPDATE_FILE } from "core/apollo/graphql";
import {
  FILE_CATEGORY_AND,
  FILE_DELETE_QUERY_WITHOUT_FORM_SIDE_EFFECTS,
  FILE_SCOPE,
  FILE_SHARE_MODE_NONE,
  FILE_SHARE_MODE_SELECTED,
  RECARE_FILE_TYPE,
  TRACK_EVENTS,
} from "core/consts";
import { PATHS } from "core/consts/pages";
import api from "core/model/api";
import { useIsDocsAndExtractActive } from "core/model/careseekers";
import { getErrorMessage } from "core/model/utils/errors";
import { computeSealdDisplayId, encryptForSession } from "core/seald";
import { useSealdContext } from "core/seald/SealdContext";
import { Auction, AuctionRequest, FileParams, Filev2 } from "core/types";
import { differenceInMilliseconds, getTime } from "date-fns";
import {
  GeneralListAction,
  SetAction,
} from "ds_legacy/components/Tables/GeneralTable/MenuActions";
import { useAuctionRequestEventsContext } from "dsl/atoms/AuctionRequestEventsContext";
import { encryptJSON } from "dsl/organisms/Encryption/EncryptionJob";
import { FileUploadSubmitHandler } from "dsl/organisms/UploadFilesModal";
import { UploadedFileResult } from "dsl/organisms/UploadFilesModal/Screens/FileUploadScreen";
import { Dispatch, SetStateAction, useCallback, useEffect } from "react";
import { useMatch } from "react-router-dom";
import { useTracking } from "react-tracking";
import {
  useLoggedCareprovider,
  useLoggedCareseeker,
} from "reduxentities/hooks";
import { v4 as uuid } from "uuid";
import { FileFeatureData } from "./useFilesFeature";

export type FileUploadStates = Record<string, FileUploadState>;

export type FileUploadState = {
  cancel: () => Promise<void>;
  data: UploadedFileResult;
  error: { message: string; status: number } | null;
  fileId: Filev2["id"] | undefined;
  isCancelled: boolean;
  loading: boolean;
  timeout: ReturnType<typeof setTimeout> | undefined;
};

export const CANCEL_ERROR = "Cancelled";

const REMOVE_FILE_UPLOAD_SUCCESS_TIMEOUT = 8000;
const REMOVE_FILE_UPLOAD_CANCEL_TIMEOUT = 8000;

export const useIsEligiblePage = () => {
  const isOldEligiblePage = !!useMatch(
    `${PATHS.APP.PATIENT_REHAB_FORMS_HOME}/*`,
  );
  const isNewEligiblePage = !!useMatch(
    `${PATHS.APP.PATIENT_FILES}/rehab-forms`,
  );
  return isOldEligiblePage || isNewEligiblePage;
};

export function useUploadFile({
  action,
  auction,
  auctionRequest,
  params,
  setAction,
  setUploadStates,
  uploadStates,
}: {
  auction?: Auction;
  auctionRequest?: AuctionRequest;
  params: FileParams;
  setAction?: SetAction<FileFeatureData>;
  setUploadStates: Dispatch<SetStateAction<FileUploadStates>>;
  uploadStates: FileUploadStates;
  action: GeneralListAction<FileFeatureData> | undefined;
}) {
  const isEligiblePage = useIsEligiblePage();
  const { trackEvent } = useTracking();
  const envContext = useEnvContext();
  const loggedCareseeker = useLoggedCareseeker();
  const loggedCareprovider = useLoggedCareprovider();

  const [createFile] = useCreateFileMutation({ params });
  const [updateFile] = useMutation(UPDATE_FILE);
  const [deleteFile] = useDeleteFileMutation({});

  const getReceiverDocsApiPublicKeys = useLazyGetReceiverDocsApiPublicKeys();
  const [postReceiverDocsApiFile] = usePostReceiverDocsApiFile();

  const [createEncryptionContext] = useCreateEncryptionContext();
  const createSealdSession = useSealdContext()?.createSealdSession;

  const { refetchEvents } = useAuctionRequestEventsContext();

  // preload selectable careproviders for share modal
  const [getSelectableCareprovidersLazy] = useGetSelectableCareprovidersLazy();

  const { isRedesignActive } = useIsDocsAndExtractActive();

  const handleUploadFile: FileUploadSubmitHandler = useCallback(
    async (fileData, options) => {
      const {
        category,
        category_other,
        content,
        file,
        file_scope,
        fileName,
        form_synced_with_file,
        kis_document_id_string,
        recare_file_type,
      } = fileData;
      const deleteFileParams: FileParams = isRedesignActive
        ? (`${params}&${FILE_DELETE_QUERY_WITHOUT_FORM_SIDE_EFFECTS}` as FileParams)
        : params;
      const id = `file_${uuid()}_${new Date().getTime()}`;

      try {
        const uploadStartTime = getTime(new Date());

        options?.onStarted?.();

        const fileState: FileUploadState = {
          cancel: async () => {
            fileState.isCancelled = true;
            setUploadStates((prev) => ({
              ...prev,
              [id]: { ...prev[id], isCancelled: true, loading: false },
            }));
          },
          data: fileData,
          error: null,
          fileId: -1,
          isCancelled: false,
          loading: true,
          timeout: undefined,
        };

        setUploadStates((prev) => {
          return {
            ...prev,
            [id]: fileState,
          };
        });

        const isMessenger = params.includes("auction_request_id");
        const createdById = loggedCareprovider?.id || loggedCareseeker?.id;
        if (!createdById || (loggedCareprovider?.id && loggedCareseeker?.id)) {
          throw new Error(
            "[handleUploadedFile] The createdById must be defined. Either careseeker or careprovider must be defined, but not both!",
          );
        }

        if (fileState.isCancelled) {
          throw new Error(CANCEL_ERROR);
        }

        const fileToCreate = {
          careprovider_id: loggedCareprovider?.id,
          careseeker_id: loggedCareseeker?.id,
          category,
          category_other:
            category === FILE_CATEGORY_AND ? category_other : undefined,
          file_scope:
            file_scope ||
            (isEligiblePage
              ? FILE_SCOPE.ELIGIBILITY
              : FILE_SCOPE.PATIENT_FILES),
          file_size: file.size,
          file_type: file.type,
          form_synced_with_file: !!form_synced_with_file,
          kis_document_id_string,
          recare_file_type: recare_file_type ?? RECARE_FILE_TYPE.UNDEFINED,
        };

        let { data: createFileData } = await createFile(fileToCreate);

        if (!createFileData?.file) {
          throw new Error("[handleUploadedFile] Couldn't create file!");
        }

        if (createFileData.file.seald_encryption_context?.seald_id) {
          // form file already exists, delete it to create a new one
          await deleteFile(undefined, {
            id: createFileData.file.id,
            params: deleteFileParams,
          });

          const { data: newFileData } = await createFile(fileToCreate);
          if (!newFileData?.file) {
            throw new Error("[handleUploadedFile] Couldn't recreate file");
          }

          createFileData = {
            ...newFileData,
            file: {
              ...newFileData.file,
              // take the existing share mode from the original file
              share_mode: createFileData.file.share_mode,
            },
          };
        }

        const fileId = createFileData.file.id;

        fileState.fileId = fileId;
        setUploadStates((prev) => {
          const updatedFileState: FileUploadState = {
            ...prev[id],
            cancel: async () => {
              try {
                fileState.isCancelled = true;
                setUploadStates((prev) => ({
                  ...prev,
                  [id]: {
                    ...prev[id],
                    isCancelled: true,
                    loading: false,
                    timeout: setTimeout(() => {
                      setUploadStates((prev) => {
                        const updatedState = { ...prev };
                        delete updatedState[id];
                        return updatedState;
                      });
                    }, REMOVE_FILE_UPLOAD_CANCEL_TIMEOUT),
                  },
                }));
                await deleteFile(undefined, {
                  id: fileId,
                  params: deleteFileParams,
                });
              } catch (err) {
                console.error(
                  `[Cancelled file upload - delete file failed]: Unable to delete file ${fileId} - ${
                    (err as Error).message
                  }`,
                );
              }
            },
            fileId,
          };

          return {
            ...prev,
            [id]: updatedFileState,
          };
        });

        const entityUsersDisplayIds = await new Promise<string[]>((resolve) => {
          if (fileState.isCancelled) {
            throw new Error(CANCEL_ERROR);
          }

          const entityUsersDisplayIds = [];

          if (isMessenger) {
            const careproviderId = auctionRequest?.careprovider_id;
            const careseekerId =
              auctionRequest?.auction?.patient?.careseeker?.id;

            if (!careproviderId || !careseekerId) {
              throw new Error(
                "[handleUploadedFile] careprovider and careseeker ID must be defined!",
              );
            }

            entityUsersDisplayIds.push(
              computeSealdDisplayId({
                envContext,
                id: careseekerId,
                type: "careseeker",
              }),
              computeSealdDisplayId({
                envContext,
                id: careproviderId,
                type: "careprovider",
              }),
            );
          } else {
            entityUsersDisplayIds.push(
              computeSealdDisplayId({
                envContext,
                id: createdById,
                type: "careseeker",
              }),
            );
          }

          resolve(entityUsersDisplayIds);
        });

        if (entityUsersDisplayIds?.length < 1) {
          throw new Error(
            "[handleUploadedFile] entityUserDisplayId must be defined!",
          );
        }

        if (fileState.isCancelled) {
          throw new Error(CANCEL_ERROR);
        }

        const loggedGroupDisplayId = loggedCareseeker?.id
          ? computeSealdDisplayId({
              envContext,
              id: loggedCareseeker.id,
              type: "careseeker",
            })
          : loggedCareprovider?.id
          ? computeSealdDisplayId({
              envContext,
              id: loggedCareprovider.id,
              type: "careprovider",
            })
          : null;

        if (!loggedGroupDisplayId) {
          throw new Error(
            "[handleUploadedFile] loggedGroupDisplayId must be defined!",
          );
        }

        const sealdSession = await createSealdSession?.({
          checkSealdGroupAccess: api.crypto.checkSealdGroupAccess,
          createEncryptionContext,
          createSealdAccess: api.crypto.createSealdAccess,
          entity: createFileData.file,
          entityType: "file",
          entityUsersDisplayIds,
          loggedGroupDisplayId,
        });

        if (!sealdSession?.seald_id) {
          throw new Error(
            "[handleUploadedFile] Couldn't create seald session!",
          );
        }

        if (fileState.isCancelled) {
          throw new Error(CANCEL_ERROR);
        }

        const [encryptedFileName, encryptedContent] = await Promise.all(
          [fileName, content].map((message) =>
            encryptForSession({
              context: `handleUploadFile - ${
                fileName ? "fileName" : "content"
              }`,
              message,
              sessionId: sealdSession.seald_id,
            }),
          ),
        );

        if (!encryptedFileName || !encryptedContent) {
          throw new Error("[handleUploadedFile] Couldn't encrypt messages!");
        }

        if (fileState.isCancelled) {
          throw new Error(CANCEL_ERROR);
        }

        const { data: updateFileData } = await updateFile({
          variables: {
            id: fileId,
            input: {
              file_content: {
                content: null,
                iv: null,
                seald_content: encryptedContent,
              },
              file_name: {
                content: null,
                iv: null,
                seald_content: encryptedFileName,
              },
              form_synced_with_file: !!form_synced_with_file,
              share_mode: isMessenger
                ? FILE_SHARE_MODE_SELECTED
                : fileData.share_mode ??
                  createFileData.file.share_mode ??
                  FILE_SHARE_MODE_NONE,
            },
            params,
          },
        });

        if (!updateFileData?.file) {
          throw new Error("[handleUploadedFile] Couldn't update file!");
        }

        if (fileState.isCancelled) {
          throw new Error(CANCEL_ERROR);
        }

        refetchEvents?.();

        if (fileState.isCancelled) {
          throw new Error(CANCEL_ERROR);
        }

        // On Success
        const uploadFileDuration = differenceInMilliseconds(
          getTime(new Date()),
          uploadStartTime,
        );
        trackEvent({
          duration: uploadFileDuration,
          file_implementation: "HIS",
          name: TRACK_EVENTS.UPLOAD_FILE_HIS_DURATION,
        });
        setUploadStates((prev) => {
          const updatedFileState: FileUploadState = {
            ...prev[id],
            loading: false,
            timeout: setTimeout(() => {
              setUploadStates((prev) => {
                const updatedState = { ...prev };
                delete updatedState[id];
                return updatedState;
              });
            }, REMOVE_FILE_UPLOAD_SUCCESS_TIMEOUT),
          };
          return {
            ...prev,
            [id]: updatedFileState,
          };
        });

        if (auction?.id && loggedCareseeker?.id) {
          // Upload to receiver docs API integrations in background to prevent blocking
          // on delay proportional to the number of integrations
          (async () => {
            try {
              const receiverDocsApiPublicKeys =
                await getReceiverDocsApiPublicKeys(auction.id);
              if (!receiverDocsApiPublicKeys?.length) {
                return;
              }

              await Promise.all(
                receiverDocsApiPublicKeys.map(
                  async ({
                    id: integrationId,
                    public_key,
                  }: {
                    id: number;
                    public_key: string;
                  }) => {
                    try {
                      const encryptedContent = await encryptJSON({
                        encryptionMaterial: {
                          encryption_public_key: public_key,
                        },
                        json: content,
                      });
                      await postReceiverDocsApiFile(
                        {
                          encrypted_aes_secrets:
                            encryptedContent.encrypted_aes_secrets,
                          encrypted_content:
                            encryptedContent.aes_encrypted_data,
                          file_id: fileId,
                        },
                        { integrationId },
                      );
                    } catch (err) {
                      console.error(
                        `[Failed to upload file to receiver docs api]: Unable to upload file ${fileId} to integration ${integrationId} - ${
                          (err as Error).message
                        }`,
                      );
                    }
                  },
                ),
              );
            } catch (err) {
              console.error(
                `[Failed to fetch receiver docs api public keys]: Unable to fetch public keys for auction ${
                  auction.id
                } - ${(err as Error).message}`,
              );
            }
          })();
        }

        options?.onComplete?.(updateFileData.file);

        return updateFileData.file;
      } catch (err) {
        setUploadStates((prev) => {
          if (!prev[id] || prev[id].isCancelled) {
            // file upload state has been deleted or cancelled
            return prev;
          }

          const updatedFileState: FileUploadState = {
            ...prev[id],
            error: {
              message: getErrorMessage(err),
              status: (err as any).status || 500,
            },
            loading: false,
          };
          return {
            ...prev,
            [id]: updatedFileState,
          };
        });

        console.error(err);
        return null;
      }
    },
    [
      action,
      auction,
      auctionRequest,
      createEncryptionContext,
      createFile,
      createSealdSession,
      deleteFile,
      envContext,
      getSelectableCareprovidersLazy,
      isEligiblePage,
      loggedCareprovider,
      loggedCareseeker,
      params,
      refetchEvents,
      setAction,
      setUploadStates,
      trackEvent,
      updateFile,
    ],
  );

  useEffect(() => {
    return () => {
      Object.values(uploadStates).forEach(({ timeout }) => {
        if (timeout) clearTimeout(timeout);
      });
    };
  }, []);

  return { handleUploadFile };
}
